import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle In-Dialog-Tour fuer den Bulk-Veroeffentlichen-Dialog
 * (modules/admin/component/bulk-publish-shifts-dialog). Wird via Hilfe-Button
 * (`<app-dialog-help-button>`) im Hero-Header manuell gestartet. Erklaert in
 * 4 Schritten: Welcome (Entwurf → live, Team wird benachrichtigt) → Tage →
 * Abteilungen & Mitarbeiter (UND-Verknuepfung, Schicht-Chips; MA ohne Entwurf
 * stehen ausgegraut/ohne Haekchen unten) → Veroeffentlichen (Live-Zaehler
 * erstmals/erneut, Versand).
 *
 * Konstruktive Spiegel-Tour zu `BULK_DELETE_SHIFTS_HELP_TOUR`: erklaerungs-
 * getrieben (`nextCondition: 'manual'`), aber ohne `tone: 'warning'` und ohne
 * Accordion-Schritt — der Publish-Dialog hat keine "Erweiterte Optionen".
 *
 * Fachliche Quelle (Backend `BulkPublishShiftsCommandHandler`): Veroeffentlichen
 * setzt `WorkShiftStatus` Draft → Published, inkrementiert `PublicationCount`
 * und versendet pro betroffenem Mitarbeiter eine `WeeklyShiftPublished`-
 * Benachrichtigung (nicht optional). Filter Days/DepartmentIds/EmployeeIds sind
 * UND-verknuepft; `null` = alle. Re-Publish (`PublicationCount > 0`) ist non-
 * destruktiv. Die Vorschau laeuft als DryRun. Ausgewaehlte Mitarbeiter ohne
 * Entwuerfe werden still gefiltert und nicht benachrichtigt (Hint QW-12).
 */
export const BULK_PUBLISH_SHIFTS_HELP_TOUR: TourConfig = {
  id: TourFeature.BulkPublishShiftsHelp,
  version: 1,
  title: 'BULK_PUBLISH_SHIFTS_HELP_TOUR_TITLE',
  description: 'BULK_PUBLISH_SHIFTS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Help-Hub-Start: zur Schichtzuweisung navigieren, dann den
  // "Veroeffentlichen"-Toolbar-Button klicken. Konstruktive Aktion (Re-Publish
  // non-destruktiv) → `auto` ist OK. Aus dem In-Dialog-`?`-Button uebersprungen.
  launchContext: {
    route: '/planner',
    opens: { openerAnchor: AnchorId.PublishButton, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 50,
  // Bewusst 'feature-discovery' (NICHT 'contextual-help'): klassische Guided
  // Tour mit Spotlight + Voice-Over, nicht der HelpModeService mit (i)-Icons.
  // Begruendung siehe AUTO_SUGGEST_HELP_TOUR.
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'BULK_PUBLISH_SHIFTS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Welcome erklaert den GANZEN Dialog — Spotlight am .bds-Wrapper.
          anchor: AnchorId.BulkPublishDialog,
          title: 'TUTORIAL.BULK_PUBLISH_SHIFTS.WELCOME.TITLE',
          content: 'TUTORIAL.BULK_PUBLISH_SHIFTS.WELCOME.CONTENT',
          placement: 'auto',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'days',
          anchor: AnchorId.BulkPublishDays,
          title: 'TUTORIAL.BULK_PUBLISH_SHIFTS.DAYS.TITLE',
          content: 'TUTORIAL.BULK_PUBLISH_SHIFTS.DAYS.CONTENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
        },
        {
          id: 'recipients',
          // Spotlight umfasst Mitarbeiter- UND Abteilungs-Sektion: beide bilden
          // gemeinsam (mit den Tagen) die UND-verknuepfte Freigabemenge. Popover
          // ankert an der Mitarbeiter-Sektion (dort liegen die Schicht-Chips).
          anchor: AnchorId.BulkPublishEmployees,
          additionalSpotlightAnchors: [AnchorId.BulkPublishDepartments],
          title: 'TUTORIAL.BULK_PUBLISH_SHIFTS.RECIPIENTS.TITLE',
          content: 'TUTORIAL.BULK_PUBLISH_SHIFTS.RECIPIENTS.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Mitarbeiter-/Abteilungs-Sektion sind *ngIf-gebunden; ohne uebergebene
          // Mitarbeiter wird der Anker nicht gerendert. DOM-realer Check statt
          // vermutetem Zustand (siehe CONTEXTUAL_HELP_PATTERN-Stolperfallen).
          skipIf: () =>
            !document.querySelector(
              `[onboardingAnchor="${AnchorId.BulkPublishEmployees}"]`,
            ),
        },
        {
          id: 'confirm',
          // Popover ankert am Veroeffentlichen-Button (immer vorhanden, ggf.
          // disabled). Zusaetzlicher Spotlight auf die Zaehler-Leiste, die nur
          // bei Treffern erscheint — fehlt sie, faellt der Cutout still weg.
          anchor: AnchorId.BulkPublishConfirm,
          additionalSpotlightAnchors: [AnchorId.BulkPublishCounters],
          title: 'TUTORIAL.BULK_PUBLISH_SHIFTS.CONFIRM.TITLE',
          content: 'TUTORIAL.BULK_PUBLISH_SHIFTS.CONFIRM.CONTENT',
          placement: 'top',
          dialogContext: { attachToDialog: true, autoCloseOnDialogClose: true },
          nextCondition: 'manual',
          // Letzter Step: sauberer Abschluss, kein Zurueck-Button.
          showPrevButton: false,
        },
      ],
    },
  ],
};
